import { Injectable } from '@angular/core';
import { Action } from '@ngrx/store';
import { Actions, Effect } from '@ngrx/effects';
import { Observable } from 'rxjs/Observable';
import { ModuleConfiguration } from '../messages.module.config';
import * as dashboardActions from '../../pillar/dashboard/store/actions';
import * as workspaceActions from '../../pillar/workspace/store/actions';
import { ResponseMessageType } from '../../core/services/ResponseMessageType';
import * as messagesActions from '../store/actions';
import { MessagesListModel } from '../models/MessagesListModel';
import { MessagesModel } from '../models/MessagesModel';
import { DateFramedMessageModel } from '../models/DateFramedMessageModel';
import { ModelResponse } from '../../core/services/ModelResponse';
import { LoadPagePayload } from '../../pillar/common/actions/LoadPagePayload';
import { MessagesService } from './MessagesService';
import { ActivateDraftDisclaimerDescriptionAction, ActivateDraftBroadcastMessageAction, ExpireNotificationAction, ExpireBroadcastAction } from '../store/actions';
import { HttpErrorResponse } from '@angular/common/http';
import { ActionError } from '../../pillar/common/actions/ActionError';

@Injectable()
export class MessagesServiceEffects {
  onlyOnce = true;
  @Effect()
  getMessages$: Observable<Action> = this.actions$.ofType(messagesActions.GET_MESSAGES_ACTION)
      .switchMap((action: messagesActions.GetMessagesAction) => this.messagesService.getMessages()
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<MessagesModel>(error)))
      .map((response: ModelResponse<MessagesModel>) => {
      if (response.hasError) {
        return new ActionError(action, response.messages.map(m => m.value));
      }
        return new messagesActions.GetMessagesSuccessAction({ messages: response.model });
      }));

  @Effect()
  activateDisclaimer$: Observable<Action> = this.actions$.ofType(messagesActions.ACTIVATE_DRAFT_DISCLAIMER_DESCRIPTION_ACTION)
      .switchMap((a: ActivateDraftDisclaimerDescriptionAction) => this.messagesService.activateDisclaimer()
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<string>(error)))
      .map((response: ModelResponse<string>) => {
        if (response.hasError) {
          return new dashboardActions.TileErrorsAction(ModuleConfiguration.moduleName, MessagesModel, { errors: response.messages.map(m => m.value) });
        }
        return new messagesActions.ActivateDraftDisclaimerDescriptionSuccessAction({ activatedDisclaimer: response.model });
      }));

  @Effect()
  activateBroadcast$: Observable<Action> = this.actions$.ofType(messagesActions.ACTIVATE_DRAFT_BROADCAST_MESSAGE_ACTION)
      .switchMap((a: ActivateDraftBroadcastMessageAction) => this.messagesService.activateBroadcast()
        .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<DateFramedMessageModel>(error)))
        .map((response: ModelResponse<DateFramedMessageModel>) => {
        if (response.hasError) {
          return new dashboardActions.TileErrorsAction(ModuleConfiguration.moduleName, MessagesModel, { errors: response.messages.map(m => m.value) });
        }
        return new messagesActions.ActivateDraftBroadcastMessageSuccessAction({ activatedBroadcast: response.model });
      }));

  @Effect()
  expireBroadcast$: Observable<Action> = this.actions$.ofType(messagesActions.EXPIRE_BROADCAST_ACTION)
      .switchMap((a: ExpireBroadcastAction) => this.messagesService.expireBroadcast()
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<DateFramedMessageModel>(error)))
      .map((response: ModelResponse<DateFramedMessageModel>) => {
        if (response.hasError) {
          return new dashboardActions.TileErrorsAction(ModuleConfiguration.moduleName, MessagesModel, { errors: response.messages.map(m => m.value) });
        }
        return new messagesActions.ExpireBroadcastSuccessAction({ expiredBroadcast: response.model });
      }));

  @Effect()
  reviseDraftDisclaimerDescription$: Observable<Action> = this.actions$.ofType(workspaceActions.updateModelType(ModuleConfiguration.moduleName, MessagesModel, 'draftDisclaimerDescription'))
      .switchMap((action: workspaceActions.UpdateModelAction<MessagesModel, string>) => this.messagesService.reviseDraftDisclaimerDescription(action.payload.key, action.payload.model)
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<string>(error)))
      .map((response: ModelResponse<string>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: action,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.UpdateModelSuccessAction(ModuleConfiguration.moduleName, MessagesModel, "draftDisclaimerDescription", { key: "", model: response.model, control: action.payload.control });
      }));

  @Effect()
  expireNotification$: Observable<Action> = this.actions$.ofType(messagesActions.EXPIRE_NOTIFICATION_ACTION)
    .switchMap((a: ExpireNotificationAction) => this.messagesService.expireNotification( a.payload.notification)
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<DateFramedMessageModel>(error)))
      .map((response: ModelResponse<DateFramedMessageModel>) => {
        if (response.hasError) {
          return new ActionError(a, response.messages.map(m => m.value));
        }
        return new messagesActions.ExpireNotificationSuccessAction({ expiredNotification: response.model });
      }));

  @Effect()
  updateDraftBroadcastMessage: Observable<Action> = this.actions$.ofType(workspaceActions.updateModelType<MessagesModel, DateFramedMessageModel>(ModuleConfiguration.moduleName, MessagesModel, 'draftBroadcastMessage'))
      .switchMap((a: workspaceActions.UpdateModelAction<MessagesModel, DateFramedMessageModel>) => this.messagesService.reviseDraftBroadcastMessage(a.payload.key, a.payload.model)
        .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<DateFramedMessageModel>(error)))
        .map((response: ModelResponse<DateFramedMessageModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.UpdateModelSuccessAction(ModuleConfiguration.moduleName,
          MessagesModel,
          'draftBroadcastMessage',
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));

  @Effect()
  updateActiveBroadcastMessage: Observable<Action> = this.actions$.ofType(workspaceActions.updateModelType<MessagesModel, DateFramedMessageModel>(ModuleConfiguration.moduleName, MessagesModel, 'activeBroadcastMessage'))
    .switchMap((a: workspaceActions.UpdateModelAction<MessagesModel, DateFramedMessageModel>) => this.messagesService.reviseActiveBroadcastMessage(a.payload.key, a.payload.model)
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<DateFramedMessageModel>(error)))
      .map((response: ModelResponse<DateFramedMessageModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.UpdateModelSuccessAction(ModuleConfiguration.moduleName,
          MessagesModel,
          'activeBroadcastMessage',
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));

  @Effect()
  addNotification: Observable<Action> = this.actions$.ofType(workspaceActions.updateModelType<MessagesModel, DateFramedMessageModel>(ModuleConfiguration.moduleName, MessagesModel, 'notification'))
      .filter((a: workspaceActions.UpdateModelAction<MessagesModel, DateFramedMessageModel>) => a.payload.model.value !== undefined && a.payload.key !== '')
      .switchMap((a: workspaceActions.UpdateModelAction<MessagesModel, DateFramedMessageModel>) => this.messagesService.addNotification(a.payload.key, a.payload.model)
        .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<DateFramedMessageModel>(error)))
        .map((response: ModelResponse<DateFramedMessageModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.UpdateModelSuccessAction(ModuleConfiguration.moduleName,
          MessagesModel,
          'notification',
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));

  @Effect()
  reviseNotification: Observable<Action> = this.actions$.ofType(workspaceActions.updateModelType<MessagesModel, DateFramedMessageModel>(ModuleConfiguration.moduleName, MessagesModel, 'notification'))
    .filter((a: workspaceActions.UpdateModelAction<MessagesModel, DateFramedMessageModel>) => a.payload.model.value === undefined && a.payload.key !== '')
    .switchMap((a: workspaceActions.UpdateModelAction<MessagesModel, DateFramedMessageModel>) => this.messagesService.reviseNotification(a.payload.key, a.payload.model)
      .catch((error: HttpErrorResponse) => Observable.of(new ModelResponse<DateFramedMessageModel>(error)))
      .map((response: ModelResponse<DateFramedMessageModel>) => {
        if (response.hasError) {
          return new workspaceActions.UpdateModelErrorAction(
            {
              action: a,
              errors: response.messages.filter(m => m.type === ResponseMessageType.Error).map(m => m.value)
            });
        }
        return new workspaceActions.UpdateModelSuccessAction(ModuleConfiguration.moduleName,
          MessagesModel,
          'notification',
          {
            key: a.payload.key,
            model: response.model,
            control: a.payload.control
          });
      }));
  constructor(
    private messagesService: MessagesService,
    private actions$: Actions
  ) {
  }
}



// WEBPACK FOOTER //
// ./src/app/messages/services/MessagesService.effects.ts